import type { MessageDescriptor } from 'react-intl';
import { useIntl } from 'react-intl';
import { BaseTooltipPlacement } from '@cbhq/cds-common/types';
import { Box } from '@cbhq/cds-web/layout/Box';
import { VStack } from '@cbhq/cds-web/layout/VStack';
import { Tooltip } from '@cbhq/cds-web/overlays/tooltip/Tooltip';
import { Text } from '@cbhq/cds-web/typography/Text';

const tooltipTriggerStyle: React.CSSProperties = {
  textDecoration: 'underline dotted var(--color-bgLine)',
  textUnderlineOffset: 4,
  cursor: 'pointer',
};

type CountOrSingleCellProps = {
  items: string[];
  /** Message descriptor with a `{count}` variable, e.g. "{count} roles" */
  countMessage: MessageDescriptor;
  color?: React.ComponentProps<typeof Text>['color'];
  font?: Extract<React.ComponentProps<typeof Text>['font'], 'label2' | 'body'>;
  /**
   * Render the count label ("1 entity") for a single item instead of the item's
   * name. The header uses this so a lone entity reads as "1 entity"; table cells
   * leave it off to keep showing the single name.
   */
  alwaysShowCount?: boolean;
};

/**
 * Shows a single item as plain text, or a count label with a tooltip listing
 * all items when there are multiple. Falls back to "—" when items is empty.
 */
export function CountOrSingleCell({
  items,
  countMessage,
  color,
  font = 'label2',
  alwaysShowCount = false,
}: CountOrSingleCellProps) {
  const { formatMessage } = useIntl();

  if (items.length > 1 || (items.length === 1 && alwaysShowCount)) {
    return (
      <Box width="fit-content">
        <Tooltip
          content={
            <VStack gap={1}>
              {items.map((item) => (
                <Text key={item} font="label2">
                  {item}
                </Text>
              ))}
            </VStack>
          }
          placement={'bottom-start' as BaseTooltipPlacement}
        >
          <Text font={font} color={color} display="block" style={tooltipTriggerStyle}>
            {formatMessage(countMessage, { count: items.length })}
          </Text>
        </Tooltip>
      </Box>
    );
  }

  if (items.length === 1) {
    return (
      <Text font={font} color={color}>
        {items[0]}
      </Text>
    );
  }

  return (
    <Text font={font} color={color}>
      —
    </Text>
  );
}
